import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Phone, AlertTriangle, Ruler, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PRICES, BENNES, CHOIX_DECHETS, volumesDisponibles, prixParticulier, prefillCommande } from '../data/offre';

const BenneChooser = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dechet, setDechet] = useState(null);
  const [volume, setVolume] = useState(null);

  const volumes = dechet?.value ? volumesDisponibles(dechet.value) : [];
  const prix = dechet?.value && volume ? PRICES[volume][dechet.value] : null;

  const choisirDechet = (d) => {
    setDechet(d);
    setVolume(null);
  };

  const commander = () => {
    prefillCommande(volume, dechet.value);
    navigate(user ? '/mon-compte' : '/commander-sans-compte', { state: { volume } });
  };

  return (
    <section id="quelle-benne" className="py-20 bg-white scroll-mt-28" data-testid="chooser-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Badge className="mb-4 bg-green-100 text-green-700 hover:bg-green-100">Aide au choix</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Quelle benne choisir ?</h2>
          <p className="text-base md:text-lg text-gray-600">Deux questions pour trouver la benne proposée pour vos déchets, avec son tarif de départ.</p>
        </div>

        <div className="rounded-2xl border-2 border-gray-100 shadow-lg p-6 sm:p-8 space-y-8">
          <div>
            <p className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">1. Quel type de déchets ?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="chooser-dechets">
              {CHOIX_DECHETS.map((d) => (
                <button
                  key={d.slug}
                  type="button"
                  onClick={() => choisirDechet(d)}
                  className={`text-left p-3 rounded-xl border-2 transition-colors ${dechet?.slug === d.slug ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                  data-testid={`chooser-type-${d.slug}`}
                >
                  <span className="block font-bold text-gray-900 text-sm">{d.label}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">{d.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {dechet && !dechet.value && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-xl bg-amber-50 border border-amber-200" data-testid="chooser-autre-msg">
              <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-gray-900">Ces déchets ne sont pas acceptés en benne standard.</p>
                <p className="text-sm text-gray-600">Contactez-nous pour étudier votre besoin avant toute commande.</p>
              </div>
              <a href="tel:0745558835" className="inline-flex items-center justify-center gap-2 rounded-md bg-gray-900 text-white px-5 py-3 font-bold text-sm hover:bg-gray-800 transition-colors" data-testid="chooser-contact-link">
                <Phone className="h-4 w-4" /> 07 45 55 88 35
              </a>
            </div>
          )}

          {dechet?.value && (
            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <p className="text-sm font-bold text-gray-900 uppercase tracking-wide">2. Quel volume ?</p>
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Info className="h-3.5 w-3.5" /> {dechet.note}</p>
              </div>
              <div className={`grid gap-3 ${volumes.length > 2 ? 'sm:grid-cols-3 lg:grid-cols-5' : 'sm:grid-cols-2'}`} data-testid="chooser-volumes">
                {volumes.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVolume(v)}
                    className={`text-left p-4 rounded-xl border-2 transition-colors ${volume === v ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                    data-testid={`chooser-volume-${v.replace('³', '3')}`}
                  >
                    <span className="block text-xl font-bold text-gray-900">{v}</span>
                    <span className="block text-xs text-gray-600 mt-1">{BENNES[v].description}</span>
                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500"><Ruler className="h-3 w-3" /> {BENNES[v].dimensions}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">Indication : le volume utile dépend de la nature et du compactage des déchets. En cas de doute, choisissez le volume supérieur ou appelez-nous.</p>
            </div>
          )}

          {prix && (
            <div className="flex flex-col md:flex-row md:items-center gap-5 p-5 rounded-xl bg-gray-950 text-white" data-testid="chooser-result">
              <div className="flex-1">
                <p className="text-sm text-gray-300">Benne proposée</p>
                <p className="text-2xl font-bold" data-testid="chooser-result-title">Benne {volume} · {dechet.label}</p>
                <p className="text-sm text-gray-300 mt-1">
                  Dès <span className="font-bold text-green-400" data-testid="chooser-result-prix-pro">{prix} € HT</span> professionnel · <span className="font-bold text-white" data-testid="chooser-result-prix-part">{prixParticulier(prix)} € HT</span> particulier
                </p>
                <p className="text-xs text-gray-400 mt-1">Location 15 jours, transport et traitement inclus. Dépôt standard ; options Express et Rotation disponibles à la commande.</p>
              </div>
              <Button onClick={commander} size="lg" className="w-full md:w-auto bg-green-600 hover:bg-green-500 text-white py-6 px-8 text-base font-bold" data-testid="chooser-order-btn">
                Commander cette benne <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BenneChooser;
